import TaskItem from '@tiptap/extension-task-item';

function setCheckboxChecked(checkbox: HTMLInputElement) {
  checkbox.setAttribute('checked', 'checked');
}

function setCheckboxUnchecked(checkbox: HTMLInputElement) {
  checkbox.removeAttribute('checked');
}

function handleCheckboxChange(
  checkbox: HTMLInputElement | null,
  node: any,
  editor: any,
  getPos: any,
  onReadOnlyChecked: any
) {
  // if the editor isn't editable and we don't have a handler for
  // readonly checks we have to undo the latest change
  if (!editor.isEditable && !onReadOnlyChecked && checkbox) {
    checkbox.checked = !checkbox.checked;
    return;
  }

  const { checked } = checkbox ?? {};

  if (editor.isEditable && typeof getPos === 'function') {
    editor
      .chain()
      .focus(undefined, { scrollIntoView: false })
      .command(({ tr }: { tr: any }) => {
        const position = getPos();
        const currentNode = tr.doc.nodeAt(position);
        tr.setNodeMarkup(position, undefined, { ...currentNode?.attrs, checked });
        return true;
      })
      .run();
  }

  if (!editor.isEditable && onReadOnlyChecked) {
    if (!onReadOnlyChecked(node, checked) && checkbox) {
      checkbox.checked = !checkbox.checked;
    }
  }
}

export default TaskItem.extend({
  addKeyboardShortcuts() {
    const shortcuts = {
      Enter: () => {
        if (this.editor.storage.mention.mentionDropdownActive) {
          return false;
        }
        return this.editor.commands.splitListItem(this.name);
      },
      'Shift-Tab': () => this.editor.commands.liftListItem(this.name),
      Backspace: ({ editor }: { editor: any }) => {
        if (editor.isActive('taskItem')) {
          const { selection } = editor.state;
          const { $from } = selection;
          const { parentOffset } = $from;

          // if the cursor is at the beginning of the task item, we want to lift the item
          if (parentOffset === 0) {
            return (
              this.editor.commands.joinBackward() ||
              this.editor.commands.liftListItem(this.name) ||
              true
            );
          }
        }
        return false;
      },
    };

    if (!this.options.nested) {
      return shortcuts;
    }

    return {
      ...shortcuts,
      Tab: ({ editor }) => {
        if (editor.storage.mention.mentionDropdownActive) {
          return false;
        }
        if (editor.isActive('taskItem')) {
          return this.editor.commands.sinkListItem(this.name) || true;
        }
        return this.editor.commands.sinkListItem(this.name);
      },
    };
  },
  addNodeView() {
    // this method is fully copied from the original TaskItem extension except for the line 33, additional tab index attribute
    return ({ node, HTMLAttributes, getPos, editor }) => {
      const listItem = document.createElement('li');
      const checkboxWrapper = document.createElement('label');
      const checkboxStyler = document.createElement('span');
      const checkbox = document.createElement('input');
      const content = document.createElement('div');

      checkboxWrapper.contentEditable = 'false';
      checkbox.type = 'checkbox';
      // only additional attributes
      checkbox.tabIndex = -1;
      checkbox.addEventListener('change', (event) => {
        return handleCheckboxChange(
          event.target as HTMLInputElement,
          node,
          editor,
          getPos,
          this.options.onReadOnlyChecked
        );
      });

      Object.entries(this.options.HTMLAttributes).forEach(([key, value]) => {
        listItem.setAttribute(key, value);
      });

      listItem.dataset.checked = node.attrs.checked;
      if (node.attrs.checked) {
        checkbox.setAttribute('checked', 'checked');
      }

      checkboxWrapper.append(checkbox, checkboxStyler);
      listItem.append(checkboxWrapper, content);

      Object.entries(HTMLAttributes).forEach(([key, value]) => {
        listItem.setAttribute(key, value);
      });

      return {
        dom: listItem,
        contentDOM: content,
        update: (updatedNode) => {
          if (updatedNode.type !== this.type) {
            return false;
          }

          listItem.dataset.checked = updatedNode.attrs.checked;
          if (updatedNode.attrs.checked) {
            setCheckboxChecked(checkbox);
          } else {
            setCheckboxUnchecked(checkbox);
          }

          return true;
        },
      };
    };
  },
});
